'use client';

/**
 * PlaceSearchSheet — search the menu, from anywhere in the place.
 *
 * The old search was a field inside a collapsible panel that also held the
 * categories, the branch's phone number and the account controls. Opening it
 * meant covering the menu with a mixed drawer, and the results appeared behind
 * it, so a guest had to close the thing they searched with to see what they
 * found.
 *
 * Here, typing filters the menu underneath live and the sheet is a thin bar the
 * guest can leave open while they scroll. There is no submit — a menu is small
 * enough that waiting for Enter only adds a step.
 */

import { useEffect, useRef, type CSSProperties } from 'react';
import { Search, X } from 'lucide-react';

import { cn } from '@/components/ui/utils';
import { zClass } from '@/lib/ui/z-layers';

export function PlaceSearchSheet({
  open,
  value,
  onChange,
  onClose,
  /** Result count for the current term. Undefined hides the counter entirely. */
  resultCount,
  placeholder = 'Search the menu',
  style,
}: {
  open: boolean;
  value: string;
  onChange: (term: string) => void;
  onClose: () => void;
  resultCount?: number;
  placeholder?: string;
  /**
   * The merchant's chrome tokens. This bar renders at the page root, OUTSIDE
   * PlaceShell, so it does not inherit them — it painted itself cream over a
   * near-black menu and read as a browser find-bar rather than part of the page.
   */
  style?: CSSProperties;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    // Focus after paint so the mobile keyboard opens with the sheet rather than
    // fighting the transition.
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const trimmed = value.trim();

  return (
    <div
      className={cn(
        'fixed inset-x-0 top-0 w-full',
        zClass('shellChrome'),
        'border-b border-[var(--color-border-subtle)]',
        'bg-[var(--color-bg-surface)] text-[var(--color-text-primary)]',
        'font-[family-name:var(--font-place-control)] shadow-lg',
      )}
      style={{ ...style, paddingTop: 'env(safe-area-inset-top)' }}
    >
      {/* 56px and the same surface as PlaceHeader, so this reads as the header
          becoming a search field rather than a foreign bar dropping over it. */}
      <div className="flex h-14 items-center gap-2 px-3">
        <Search aria-hidden="true" className="size-5 shrink-0 opacity-50" />
        <input
          ref={inputRef}
          type="search"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          aria-label={placeholder}
          className={cn(
            'min-w-0 flex-1 bg-transparent text-base',
            // No border, no ring. A global input style was drawing a rounded
            // outline in the platform's accent, which on a themed menu was an
            // orange box floating in a near-black bar.
            'border-0 outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none',
            'placeholder:text-[var(--color-text-secondary)]',
            // WebKit draws its own clear button inside type="search" — a blue
            // browser-default ✕ sitting right next to ours. Two close controls,
            // one of them un-themed and not ours to style.
            '[&::-webkit-search-cancel-button]:appearance-none',
            '[&::-webkit-search-decoration]:appearance-none',
          )}
        />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close search"
          className={cn(
            'inline-flex size-10 shrink-0 items-center justify-center rounded-full',
            'transition-colors hover:bg-[var(--color-bg-surface-hover)]',
            'outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-accent)]',
          )}
        >
          <X aria-hidden="true" className="size-5" />
        </button>
      </div>

      {/* The count is the whole feedback loop for a live filter: without it, a
          term that matches nothing looks identical to a menu that failed to
          load. Announced politely so a screen reader hears the result without
          interrupting typing. */}
      {trimmed && typeof resultCount === 'number' ? (
        <p
          aria-live="polite"
          className="px-4 pb-2 text-xs text-[var(--color-text-secondary)]"
        >
          {resultCount === 0 ? (
            <>
              Nothing on the menu matches{' '}
              <span className="font-semibold text-[var(--color-text-primary)]">{trimmed}</span>.
              Try a shorter word.
            </>
          ) : (
            <>
              <span className="font-[family-name:var(--font-place-data)] tabular-nums">
                {resultCount}
              </span>{' '}
              {resultCount === 1 ? 'item matches' : 'items match'}{' '}
              <span className="font-semibold text-[var(--color-text-primary)]">{trimmed}</span>
            </>
          )}
        </p>
      ) : null}
    </div>
  );
}
